import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Agreement, Client, Contact, PartyRole, SavedLocation } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import {
  useAccountByPairApi,
  useAgreementListApi,
  useClientApi,
  useClientListApi,
  useClientUpdateApi,
  useContactListApi,
  useSavedLocationListApi,
} from '@hero/api-hooks';
import {
  useClientWorkspaceStore,
  useCurrentUser,
  VISIBLE_CLIENT_WORKSPACE_TABS,
  type ClientWorkspaceTab,
  type UseOrderAgainConfig,
} from '@hero/hooks-shared';
import { useClientWorkspaceUrlSync, useNav } from '@hero/web-hooks';
import { Button, Spinner } from '@hero/web-components';
import { heroEmblemWhite } from '@hero/web-assets';
import {
  BuildingIcon,
  ContactIcon,
  InvoicesNavIcon,
  LoadsNavIcon,
  MapPinnedIcon,
  PlusIcon,
  RouteIcon,
  SearchIcon,
  TruckIcon,
  UserIcon,
  UsersIcon,
} from '@hero/web-icons';
import { SharedList } from '../../../app/List/List';
import { ContactCard } from '../../Contact/Card/ContactCard';
import { SavedLocationCard } from '../../SavedLocation/Card/SavedLocationCard';
import { OrderTableManager } from '../../Order/Table/OrderTableManager';
import { OrderRouteWithMap } from '../../Order/Route/OrderRouteWithMap';
import { ContactEditModal } from '../../Contact/Modal/ContactEditModal';
import { LocationEditModal } from '../../SavedLocation/Modal/SavedLocationEditModal';
import { AgreementSummaryCard } from '../../Agreement/Card/AgreementSummaryCard';
import { AgreementDrawer } from '../../Agreement/Drawer/AgreementDrawer';
import { AgreementEditDrawer } from '../../Agreement/Drawer/AgreementEditDrawer';
import { ClientEditModal } from '../Modal/ClientEditModal';
import { ClientLegalDrawer, EditClientLinkModal, EditClientSettingsModal } from '../EditSection';
import { computeClientStatus } from '../ClientPeekInfo';
import { ClientInfoTab, ClientPricingTab } from './tabs';
import { EditableField } from '../../../app/EditableField';
import { AccountPage } from '../../Account/Page/AccountPage';
import { EligibleLegsDrawer } from '../../Invoice/EligibleLegs/EligibleLegsDrawer';

const COMING_SOON_TABS: ReadonlySet<ClientWorkspaceTab> = new Set();

export interface ClientWorkspacePageProps {
  displayForUser: 'customer' | 'carrier';
  /**
   * Optional reorder wiring threaded into the embedded `OrderTableManager`.
   * Hosts that want the "Order Again" affordance on the CRM Orders tab
   * (and the row-level reorder chip) pass their app-level config here
   * — same shape used on `/my-orders`.
   */
  orderAgainConfig?: UseOrderAgainConfig;
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function ClientAvatar({ name, size = 36 }: { name: string; size?: number }) {
  const initials = (name ?? '')
    .split(' ')
    .map(p => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="flex-shrink-0 rounded-md bg-gray-100 text-gray-700 flex items-center justify-center font-semibold"
      style={{ width: size, height: size, fontSize: size <= 36 ? 12 : 18 }}>
      {initials || 'C'}
    </div>
  );
}

interface ClientListRowProps {
  client: Client;
  selected: boolean;
  onSelect: () => void;
  rolesLabel: string;
}

function ClientListRow({ client, selected, onSelect, rolesLabel }: ClientListRowProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center gap-3 p-2.5 rounded-md text-start transition-colors ${
        selected
          ? 'bg-primary/5 border border-primary/40'
          : 'border border-transparent hover:bg-gray-50'
      }`}>
      <ClientAvatar name={client.name ?? ''} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-900 truncate">{client.name || '—'}</div>
        {rolesLabel ? (
          <div className="text-xs text-muted-foreground truncate">{rolesLabel}</div>
        ) : null}
      </div>
      {selected ? (
        <span className="text-[11px] font-medium text-primary uppercase tracking-wide">open</span>
      ) : null}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Right pane — tab content
// ---------------------------------------------------------------------------

function ContactsTabContent({ client }: { client: Client }) {
  const { t } = useTranslation(['m-client']);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const query = useContactListApi({ customerId: client._id });
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setIsCreateOpen(true)}>
          <PlusIcon className="h-3.5 w-3.5" />
          {t('workspace.add_contact')}
        </Button>
      </div>
      <SharedList<Contact>
        query={query}
        emptyMessage={t('drawer.no_contacts')}
        className="gap-2"
        layout="grid"
        renderItem={contact => <ContactCard contact={contact} />}
      />
      <ContactEditModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        customerId={client._id}
      />
    </div>
  );
}

function PlacesTabContent({ client }: { client: Client }) {
  const { t } = useTranslation(['m-client']);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const query = useSavedLocationListApi({ clientId: client._id });
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setIsCreateOpen(true)}>
          <PlusIcon className="h-3.5 w-3.5" />
          {t('workspace.add_place')}
        </Button>
      </div>
      <SharedList<SavedLocation>
        query={query}
        emptyMessage={t('drawer.no_locations')}
        className="gap-2"
        renderItem={location => <SavedLocationCard savedLocation={location} />}
      />
      <LocationEditModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onLocationSaved={() => setIsCreateOpen(false)}
        mode="create"
        clientId={client._id}
        forceType="client"
      />
    </div>
  );
}

function OrdersTabContent({
  client,
  displayForUser,
  orderAgainConfig,
}: {
  client: Client;
  displayForUser: 'customer' | 'carrier';
  orderAgainConfig?: UseOrderAgainConfig;
}) {
  // Reuse the canonical Orders table. `scopeQuery: { clientId }`
  // narrows the existing tenant-aware list endpoint to "my orders
  // involving this client", matching the same table the user sees on
  // /my-orders. `useGlobalFilterStore=false` keeps this tab's
  // search/status/date filters local to the workspace, so flipping
  // between clients (or jumping back to /my-orders) doesn't bleed
  // state across surfaces.
  //
  // Columns mirror `MyOrdersPage` (carrier-dashboard) — `price`
  // surfaces the per-leg cost the user actually cares about on a CRM
  // workspace ("how much do I bill / pay this client?"), and `podRef`
  // is dropped because it's a back-office identifier with no value on
  // a client-scoped view (the user is reading "orders for this
  // client", not "where is my proof-of-delivery copy?"). The
  // `destinations` column uses the shared `OrderRouteWithMap` render
  // override so clicking a route opens `OrderMapModal` — same UX as
  // `/my-orders` and `/jobs`.
  //
  // NOTE: The `OrderDrawer` is **not** mounted here — the host page
  // (e.g. carrier-dashboard `CRMWorkspacePage`) already mounts a single
  // `<OrderDrawer />` for the route. Mounting one here too made
  // `useOrderDrawerStore` render the drawer twice on row clicks.
  const { t } = useTranslation('m-client');
  const { navigate } = useNav();
  const handleNewOrder = () => {
    navigate('new_order', { clientId: String(getObjectId(client._id)) });
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={handleNewOrder}>
          <PlusIcon className="size-4 me-1" />
          {t('workspace.add_order', { defaultValue: 'New order' })}
        </Button>
      </div>
      <OrderTableManager
        displayForUser={displayForUser}
        scopeQuery={{ clientId: getObjectId(client._id) }}
        useGlobalFilterStore={false}
        drawerProps={orderAgainConfig ? { orderAgainConfig } : undefined}
        columns={[
          { key: 'ref' },
          {
            key: 'destinations',
            render: order => (
              <OrderRouteWithMap order={order} vehicle={order.load?.vehicles?.[0]} />
            ),
          },
          { key: 'status' },
          { key: 'driver' },
          { key: 'price' },
        ]}
      />
    </div>
  );
}

function AgreementsTabContent({ client, orgId }: { client: Client; orgId?: string }) {
  const { t } = useTranslation(['m-client', 'm-agreement']);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [drawerAgreement, setDrawerAgreement] = useState<Agreement | null>(null);

  // Filter Agreements via the new `partyId` filter — matches the
  // denormalized `Agreement.tenants[]` array, so the agreement shows up
  // in this list whether the client sits on `parties[0]` or
  // `parties[1]`. Backend uses a single index seek on `tenants.id`.
  const query = useAgreementListApi({ partyId: client._id });

  // TEMP: hide the "Add agreement" button as soon as there's already one
  // open agreement with this counterparty. The backend currently enforces
  // a "one open contract per (org, counterparty) pair" rule
  // (`AgreementService.createNewAgreementAPI` → `findActiveOrDraftBetween`)
  // — clicking "Add" with one already in flight returns
  // `agreement_org_already_has_one`. Once the UX for "supersede / replace
  // an existing agreement" is designed we'll re-enable this and offer a
  // proper confirm flow instead of silently 4xx-ing the user.
  const existingAgreements = query.data ?? [];
  const hasOpenAgreement = existingAgreements.some(
    a => a.status === 'new' || a.status === 'active',
  );
  const canCreate = !hasOpenAgreement;

  const counterpartyRoles: PartyRole[] | undefined =
    Array.isArray(client.roles) && client.roles.length > 0
      ? (client.roles as PartyRole[])
      : undefined;

  return (
    <div className="flex flex-col gap-3">
      {canCreate ? (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setIsCreateOpen(true)}>
            <PlusIcon className="h-3.5 w-3.5" />
            {t('workspace.add_agreement')}
          </Button>
        </div>
      ) : null}
      <SharedList<Agreement>
        query={query}
        emptyMessage={t('drawer.no_agreements')}
        className="gap-2"
        renderItem={agreement => (
          <AgreementSummaryCard
            agreement={agreement}
            fallbackName={client.name}
            orgId={orgId}
            onClick={() => setDrawerAgreement(agreement)}
          />
        )}
      />

      <AgreementEditDrawer
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        defaults={{
          counterpartyId: String(client._id),
          counterpartyName: client.name,
          counterpartyRoles,
        }}
        lockedFields={['counterparty']}
        displayFor="crm"
      />

      <AgreementDrawer
        agreement={drawerAgreement}
        isOpen={!!drawerAgreement}
        onClose={() => setDrawerAgreement(null)}
      />
    </div>
  );
}

function ComingSoonStub({ tab }: { tab: ClientWorkspaceTab }) {
  const { t } = useTranslation(['m-client']);
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-md bg-gray-50 border border-dashed border-gray-200">
      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700 mb-3">
        {t('workspace.coming_soon')}
      </span>
      <p className="text-sm text-muted-foreground max-w-sm">
        {t('workspace.tab_coming_soon_hint', {
          tab: t(`workspace.tabs.${tab}`),
        })}
      </p>
    </div>
  );
}

/**
 * Bilateral Invoices/Account tab for the CRM workspace.
 *
 * Resolves the per-pair `Account` doc via {@link useAccountByPairApi}
 * — `partyA` is the viewer's org, `partyB` is the Client's linked
 * Org/Profile (when `linkedParty` is set) or the Client itself.
 *
 * Three states:
 * 1. `linkedParty` missing AND no Account doc yet → friendly empty
 *    state explaining nothing has been captured yet for this CRM
 *    entry.
 * 2. Account doc not yet created (no invoices have been captured
 *    between the two parties) → empty state with the BalanceCard
 *    in its zero-activity rendering, so the user still sees the
 *    counterparty header.
 * 3. Account doc resolved → full `AccountPage` (BalanceCard +
 *    Invoices + Transactions + Info tabs).
 *
 * The Account / AccountTransaction / Invoice drawers themselves
 * are mounted once at the carrier-dashboard CRM page root — this
 * tab only renders the per-pair surface.
 */
function AccountInvoicesTabContent({
  client,
  viewerOrgId,
  displayForUser,
}: {
  client: Client;
  viewerOrgId?: string;
  displayForUser: 'customer' | 'carrier';
}) {
  const { t } = useTranslation(['m-account', 'm-client', 'm-invoice']);
  const [createOpen, setCreateOpen] = useState(false);

  const counterpartyId = client.linkedParty?._id ?? client._id;
  const hasCounterparty = !!counterpartyId && !!viewerOrgId;

  const { data: account, isPending } = useAccountByPairApi(
    hasCounterparty
      ? {
          partyAId: getObjectId(viewerOrgId as string),
          partyBId: getObjectId(counterpartyId),
        }
      : undefined,
  );

  if (!hasCounterparty) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-md bg-gray-50 border border-dashed border-gray-200">
        <p className="text-sm text-muted-foreground max-w-sm">
          {t('m-account:tab.no_counterparty', {
            defaultValue:
              'Link this client to an organisation to view invoices and account activity.',
          })}
        </p>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex flex-col gap-3 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-medium text-gray-900">
            {t('m-account:tab.invoices_title', { defaultValue: 'Invoices' })}
          </h3>
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2">
            <PlusIcon className="size-4" />
            {t('m-invoice:create_invoice.button', { defaultValue: 'Create invoice' })}
          </Button>
        </div>
        <div className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-md bg-gray-50 border border-dashed border-gray-200">
          <p className="text-sm text-muted-foreground max-w-sm">
            {t('m-account:tab.no_account_yet', {
              defaultValue:
                'No invoices have been captured between you and this client yet.',
            })}
          </p>
        </div>
        <EligibleLegsDrawer
          clientId={counterpartyId}
          viewerOrgId={viewerOrgId}
          open={createOpen}
          onOpenChange={setCreateOpen}
        />
      </div>
    );
  }

  return (
    <AccountPage
      account={account}
      viewerOrgId={viewerOrgId}
      asRole={displayForUser === 'carrier' ? 'seller' : 'buyer'}
      counterpartyNameOverride={client.name}
      counterpartyClientId={counterpartyId}
    />
  );
}

// ---------------------------------------------------------------------------
// Workspace
// ---------------------------------------------------------------------------

export function ClientWorkspacePage({
  displayForUser,
  orderAgainConfig,
}: ClientWorkspacePageProps) {
  const { t } = useTranslation(['m-client']);
  const { orgId } = useCurrentUser();

  // URL <-> store sync (clientId + tab).
  useClientWorkspaceUrlSync();

  const selectedClientId = useClientWorkspaceStore(s => s.selectedClientId);
  const activeTab = useClientWorkspaceStore(s => s.activeTab);
  const search = useClientWorkspaceStore(s => s.search);
  const isCreateOpen = useClientWorkspaceStore(s => s.isCreateOpen);
  const editingSection = useClientWorkspaceStore(s => s.editingSection);

  const selectClient = useClientWorkspaceStore(s => s.selectClient);
  const setActiveTab = useClientWorkspaceStore(s => s.setActiveTab);
  const setSearch = useClientWorkspaceStore(s => s.setSearch);
  const openCreate = useClientWorkspaceStore(s => s.openCreate);
  const closeCreate = useClientWorkspaceStore(s => s.closeCreate);
  const closeSectionEdit = useClientWorkspaceStore(s => s.closeSectionEdit);

  const {
    data: clients,
    isLoading: clientsLoading,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    runQuery,
  } = useClientListApi({});

  const { data: selectedClient, isPending: selectedClientPending } = useClientApi(selectedClientId);

  // Agreement awareness for the hero band's overall %. The query
  // is disabled until a client is selected; once it is, we share the
  // cache key with `AgreementInfoSection` / `ClientInfoTab` so this
  // is the same network request, not a duplicate. The result is
  // folded into `computeClientStatus({ hasOpenAgreement })` below so
  // the percentage stays consistent with what the Info tab renders.
  const heroAgreementsQuery = useAgreementListApi(
    { partyId: selectedClient?._id },
    { enabled: !!selectedClient },
  );
  const heroHasOpenAgreement = useMemo<boolean | undefined>(() => {
    if (!selectedClient) return undefined;
    if (heroAgreementsQuery.isLoading || !heroAgreementsQuery.data) return undefined;
    return heroAgreementsQuery.data.some(
      a => a.status === 'new' || a.status === 'active',
    );
  }, [selectedClient, heroAgreementsQuery.data, heroAgreementsQuery.isLoading]);

  const updateClient = useClientUpdateApi();

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      runQuery({ search: value || undefined });
    },
    [runQuery, setSearch],
  );

  const handleSelect = useCallback(
    (client: Client) => {
      selectClient(String(getObjectId(client._id)));
    },
    [selectClient],
  );

  const rolesLabelFor = useCallback(
    (client: Client) =>
      (client.roles ?? [])
        .map(r => t(`roles.${r}.title`))
        .filter(Boolean)
        .join(' · '),
    [t],
  );

  const heroSubtitle = useMemo(() => {
    if (!selectedClient) return '';
    return rolesLabelFor(selectedClient);
  }, [selectedClient, rolesLabelFor]);

  const overall = selectedClient
    ? computeClientStatus(selectedClient, {
        hasOpenAgreement: heroHasOpenAgreement,
      }).overall
    : 0;

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-160px)] min-h-[640px]">
      <div className="flex flex-1 gap-4 min-h-0">
        {/* Left rail — client list */}
        <aside className="w-[300px] flex-shrink-0 flex flex-col rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">{t('workspace.title')}</h2>
            <Button variant="default" size="sm" onClick={openCreate}>
              <PlusIcon className="h-3.5 w-3.5" />
              {t('workspace.new_client')}
            </Button>
          </div>

          <div className="px-3 py-2 border-b border-gray-100">
            <div className="relative">
              <SearchIcon className="absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="search"
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder={t('workspace.search_placeholder')}
                className="w-full ps-8 pe-3 py-1.5 text-sm rounded-md border border-gray-200 bg-gray-50 focus:bg-white focus:border-primary focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-2">
            {clientsLoading && clients.length === 0 ? (
              <div className="flex justify-center items-center py-10">
                <Spinner />
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-10">
                {t('list.empty_state')}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {clients.map((client: Client) => {
                  const idStr = String(getObjectId(client._id));
                  return (
                    <ClientListRow
                      key={idStr}
                      client={client}
                      selected={selectedClientId === idStr}
                      onSelect={() => handleSelect(client)}
                      rolesLabel={rolesLabelFor(client)}
                    />
                  );
                })}
                {hasNextPage ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isFetchingNextPage}
                    onClick={() => onLoadMore?.()}
                    className="mt-2 w-full">
                    {isFetchingNextPage ? t('workspace.loading') : t('list.loading_more')}
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        </aside>

        {/* Right pane — selected client detail */}
        <section className="flex-1 min-w-0 flex flex-col rounded-lg border border-gray-200 bg-white overflow-hidden">
          {!selectedClientId ? (
            // Two distinct empty states:
            //  1. Pure-new workspace (zero clients ever) → full onboarding card
            //     that explains what a client is, the four roles supported, and
            //     prompts the user to add their first one. We only show this
            //     when the list is genuinely empty (no search, no loading) so a
            //     search returning zero matches doesn't masquerade as a
            //     fresh-workspace state.
            //  2. Has clients but none selected → lightweight "Select a client"
            //     hint, which fits the master-detail pattern.
            clients.length === 0 && !clientsLoading && !search ? (
              <EmptyClientsOnboarding onAddClient={openCreate} />
            ) : (
              <EmptySelectionState />
            )
          ) : selectedClientPending && !selectedClient ? (
            <div className="flex flex-1 items-center justify-center">
              <Spinner />
            </div>
          ) : !selectedClient ? (
            <div className="flex flex-1 items-center justify-center text-muted-foreground">
              {t('page.not_found')}
            </div>
          ) : (
            <>
              {/* Hero */}
              <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-100">
                <ClientAvatar name={selectedClient.name ?? ''} size={48} />
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <EditableField
                        value={selectedClient.name}
                        size="lg"
                        ariaLabel={t('inline_edit.edit_aria', {
                          field: t('form.name.title'),
                        })}
                        validate={v => (v.trim() ? null : t('form.name.validation'))}
                        onSave={async next => {
                          await updateClient.mutateAsync({
                            _id: selectedClient._id,
                            clientName: next,
                          });
                        }}
                      />
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary">
                      {selectedClient.status}
                    </span>
                  </div>
                  {heroSubtitle ? (
                    <p className="text-sm text-muted-foreground truncate">{heroSubtitle}</p>
                  ) : null}
                </div>
                <div className="hidden md:flex flex-col items-end gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('drawer.peek.completeness')}
                  </span>
                  <span className="text-xl font-semibold text-gray-900">{overall}%</span>
                </div>
              </div>

              {/* Tabs — renders `VISIBLE_CLIENT_WORKSPACE_TABS`, not the
                  full set. Hidden tabs (currently just `agreements`,
                  surfaced inline on the Info tab below via
                  `AgreementInfoSection`) remain valid URL destinations
                  so deep-links don't break. */}
              <div className="flex border-b border-gray-100 px-2 overflow-x-auto">
                {VISIBLE_CLIENT_WORKSPACE_TABS.map(tab => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                      activeTab === tab
                        ? 'text-primary border-primary'
                        : 'text-muted-foreground border-transparent hover:text-gray-700'
                    }`}>
                    {t(`workspace.tabs.${tab}`)}
                    {COMING_SOON_TABS.has(tab) ? (
                      <span className="ms-1.5 text-[10px] text-amber-600">●</span>
                    ) : null}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
                {activeTab === 'info' && <ClientInfoTab client={selectedClient} />}
                {activeTab === 'contacts' && <ContactsTabContent client={selectedClient} />}
                {activeTab === 'places' && <PlacesTabContent client={selectedClient} />}
                {activeTab === 'orders' && (
                  <OrdersTabContent
                    client={selectedClient}
                    displayForUser={displayForUser}
                    orderAgainConfig={orderAgainConfig}
                  />
                )}
                {activeTab === 'pricing' && (
                  <ClientPricingTab client={selectedClient} displayForUser={displayForUser} />
                )}
                {activeTab === 'agreements' && (
                  <AgreementsTabContent client={selectedClient} orgId={orgId} />
                )}
                {activeTab === 'invoices' && (
                  <AccountInvoicesTabContent
                    client={selectedClient}
                    viewerOrgId={orgId}
                    displayForUser={displayForUser}
                  />
                )}
              </div>
            </>
          )}
        </section>
      </div>

      <ClientEditModal
        isOpen={isCreateOpen}
        onClose={closeCreate}
        onClientSaved={({ client }) => {
          closeCreate();
          handleSelect(client);
        }}
      />

      {/* Per-section edit surfaces — driven by `editingSection` in the
          store. Legal opens as a side drawer (matches the financing-flow
          requirement drawers) while the rest stay as smaller modals. */}
      <ClientLegalDrawer
        client={selectedClient}
        isOpen={editingSection === 'legal'}
        onClose={closeSectionEdit}
        displayFor="crm"
      />
      <EditClientSettingsModal
        client={selectedClient}
        isOpen={editingSection === 'settings'}
        onClose={closeSectionEdit}
      />
      <EditClientLinkModal
        client={selectedClient}
        isOpen={editingSection === 'brand'}
        onClose={closeSectionEdit}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty selection
// ---------------------------------------------------------------------------

function EmptySelectionState() {
  const { t } = useTranslation(['m-client']);
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-16 gap-3">
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-muted-foreground">
        <UserIcon className="h-6 w-6" />
      </div>
      <h2 className="text-base font-semibold text-gray-900">{t('workspace.select_a_client')}</h2>
      <p className="text-sm text-muted-foreground max-w-sm">
        {t('workspace.select_a_client_hint')}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pure-new workspace onboarding
// ---------------------------------------------------------------------------

type SatellitePosition = 'top' | 'right' | 'bottom' | 'left';

/**
 * Tailwind classes are kept in a static map (not interpolated) so the
 * JIT scanner picks every class up at build time. Each entry pins one
 * cardinal of the diagram with its own `translate-` correction so the
 * card centres on the line endpoint, not on its own corner.
 */
const POSITION_CLASSES: Record<SatellitePosition, string> = {
  top: 'top-[5%]    left-1/2 -translate-x-1/2',
  right: 'top-1/2     right-[5%] -translate-y-1/2',
  bottom: 'bottom-[5%] left-1/2 -translate-x-1/2',
  left: 'top-1/2     left-[5%]  -translate-y-1/2',
};

type RoleKey = 'customers' | 'carriers' | 'brokers' | 'individuals';

interface RoleSatellite {
  key: RoleKey;
  position: SatellitePosition;
  Icon: typeof UsersIcon;
  /**
   * Soft tinted card surface, e.g. `bg-primary-50/60`. Used as the
   * card background instead of plain white so each role gets its
   * own visual identity in the network — the four satellites read
   * as a related set without needing a visible border.
   */
  cardBg: string;
  /**
   * Solid icon chip — `bg-{color} text-white`. Pairs with `cardBg`
   * so the chip looks like a coloured "node" sitting on a soft
   * tinted card rather than two competing tinted layers.
   */
  chip: string;
  /**
   * Tailwind text-color class applied to that role's connection line
   * when this satellite is hovered/focused. The line uses
   * `stroke="currentColor"` so swapping `text-{color}` is enough to
   * tint it. Each role lights up in its own identity colour so the
   * hover state visually says "this branch of the network".
   */
  lineActiveClass: string;
  /**
   * Tailwind ring-color class used on the card focus/hover ring so
   * the keyboard/focus outline matches the role's identity colour.
   */
  ringClass: string;
}

/**
 * Cardinal layout chosen so the four roles read naturally:
 *   - customers up top (most common entry point — buyers send orders),
 *   - carriers on the start side (you subcontract loads to them),
 *   - brokers on the end side (multi-tier reseller chains),
 *   - individuals on the bottom (smallest commercial relationship).
 *
 * Microcopy lives in `m-client.workspace.onboarding.roles.<key>.microcopy`
 * — short verb phrases describing what that party type *does* relative
 * to the workspace owner. Hero's network model is bidirectional, so
 * the copy is intentionally relational ("Bring you orders") rather
 * than directional ("Customers buy from carriers"), which would
 * conflict with the "any party in any role" rule from `.cursorrules`.
 */
const SATELLITES: ReadonlyArray<RoleSatellite> = [
  {
    key: 'customers',
    position: 'top',
    Icon: UsersIcon,
    cardBg: 'bg-emerald-50/60',
    chip: 'bg-emerald-600 text-white',
    lineActiveClass: 'text-emerald-600',
    ringClass: 'ring-emerald-500/40',
  },
  {
    key: 'carriers',
    position: 'left',
    Icon: TruckIcon,
    cardBg: 'bg-blue-50/60',
    chip: 'bg-blue-600 text-white',
    lineActiveClass: 'text-blue-600',
    ringClass: 'ring-blue-500/40',
  },
  {
    key: 'brokers',
    position: 'right',
    Icon: BuildingIcon,
    cardBg: 'bg-amber-50/60',
    chip: 'bg-amber-600 text-white',
    lineActiveClass: 'text-amber-600',
    ringClass: 'ring-amber-500/40',
  },
  {
    key: 'individuals',
    position: 'bottom',
    Icon: UserIcon,
    cardBg: 'bg-rose-50/60',
    chip: 'bg-rose-600 text-white',
    lineActiveClass: 'text-rose-600',
    ringClass: 'ring-rose-500/40',
  },
];

interface Benefit {
  key: 'custom_pricing' | 'invoices' | 'track_orders' | 'saved_places' | 'contacts';
  Icon: typeof RouteIcon;
}

const BENEFITS: ReadonlyArray<Benefit> = [
  { key: 'custom_pricing', Icon: RouteIcon },
  { key: 'invoices', Icon: InvoicesNavIcon },
  { key: 'track_orders', Icon: LoadsNavIcon },
  { key: 'saved_places', Icon: MapPinnedIcon },
  { key: 'contacts', Icon: ContactIcon },
];

interface EmptyClientsOnboardingProps {
  onAddClient: () => void;
}

/**
 * Right-pane onboarding shown when the workspace has zero clients.
 *
 * Layout order is intentional, top → bottom:
 *   1. Title — the hook ("Add your first client")
 *   2. Description — one sentence on what the workspace gives them
 *   3. Benefits chips — concrete unlocked features as a quiet
 *      reassurance row that sits between the value prop and the
 *      action
 *   4. CTA — the action
 *   5. Network diagram — the visual anchor at the bottom, showing
 *      the workspace owner at the centre with the four role
 *      satellites (Customers / Carriers / Brokers / Individuals)
 *      around them. Hovering or focusing a satellite lights its
 *      connection line in the role's identity colour and gives
 *      the card a subtle scale, nothing more.
 *
 * Hero's network model (`.cursorrules`) is reflected throughout:
 *   - The hub label uses the actual org name (or user name) so the
 *     diagram is personalised, not abstract.
 *   - Each satellite role is presented neutrally — any party can
 *     buy AND sell capacity, so we describe what they ARE, not
 *     which way money flows.
 *   - Brokers / customers / carriers / individuals are all first-
 *     class peers around the hub, no broker tier or hierarchy.
 */
function EmptyClientsOnboarding({ onAddClient }: EmptyClientsOnboardingProps) {
  const { t } = useTranslation(['m-client']);
  const { orgName, name } = useCurrentUser();
  const orgLabel = orgName || name || t('workspace.onboarding.your_org');

  return (
    <div className="flex-1 overflow-y-auto px-6 py-10">
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-6 text-center">
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-xl font-semibold text-gray-900">{t('workspace.onboarding.title')}</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {t('workspace.onboarding.description')}
          </p>
        </div>

        {/* Benefits as a header-less chip row — they sit between the
            description and the CTA so the eye reads value → proof →
            action without a "Plus, you get" label getting in the way. */}
        <div className="flex flex-wrap items-center justify-center gap-2">
          {BENEFITS.map(({ key, Icon }) => (
            <span
              key={key}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-100 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700">
              <Icon className="h-3.5 w-3.5 text-primary" strokeWidth={1.75} />
              {t(`workspace.onboarding.benefits.${key}`)}
            </span>
          ))}
        </div>

        <Button variant="default" size="lg" onClick={onAddClient} className="gap-2">
          <PlusIcon className="h-4 w-4" />
          {t('workspace.onboarding.cta')}
        </Button>

        {/* Network diagram lives at the bottom — copy + CTA carry the
            message; the diagram is the visual reinforcement, not the
            lead. */}
        <NetworkDiagram orgLabel={orgLabel} />
      </div>
    </div>
  );
}

interface NetworkDiagramProps {
  orgLabel: string;
}

function NetworkDiagram({ orgLabel }: NetworkDiagramProps) {
  const { t } = useTranslation(['m-client']);
  /**
   * `hovered` is intentionally lifted to the diagram (not local to
   * each satellite) because the hover state has cross-element side
   * effects: when one satellite is hovered, ITS connection line in
   * the shared SVG must light up. Pointer/focus events on the cards
   * therefore drive a single source of truth that both the line
   * renderer and the cards themselves read from.
   */
  const [hovered, setHovered] = useState<RoleKey | null>(null);

  return (
    <div className="relative w-full max-w-2xl" style={{ aspectRatio: '4 / 3' }}>
      {/* Soft primary tint backdrop — gives the diagram a "stage" so
          the hub reads as a centre of gravity rather than a card
          floating in white. */}
      <div className="absolute inset-0 rounded-[2rem] bg-primary/[0.03]" aria-hidden />

      {/* Concentric "signal" rings around the hub. Three rings with
          decreasing opacity look like the workspace radiating
          outward — visual shorthand for "the hub is the centre,
          everything connects in toward it". `aspect-square` widths
          let them stay perfect circles even as the container scales. */}
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[55%] aspect-square rounded-full border border-primary/15"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[78%] aspect-square rounded-full border border-primary/[0.08]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-full aspect-square rounded-full border border-primary/[0.04]"
        aria-hidden
      />

      <ConnectionLines hovered={hovered} />

      {SATELLITES.map(sat => (
        <SatelliteNode
          key={sat.key}
          satellite={sat}
          onActivate={() => setHovered(sat.key)}
          onDeactivate={() => setHovered(prev => (prev === sat.key ? null : prev))}
          title={t(`workspace.onboarding.roles.${sat.key}.title`)}
          microcopy={t(`workspace.onboarding.roles.${sat.key}.microcopy`)}
        />
      ))}

      {/* Hub — the workspace owner. Primary-tinted glow shadow lifts
          it off the canvas; the disc shows the Hero "H" emblem (the
          same mark used in the dashboard tab/favicon). The "YOU"
          label was dropped because the disc + the org name together
          already say "this is your workspace" without a third line
          taking vertical space. The org name is `truncate`-clamped
          with a `title` tooltip so long names like
          "Saudi Logistics Solutions Co." show as
          "Saudi Logistics S…" and reveal the full string on hover —
          keeps the hub a stable size regardless of org-name length. */}
      <div className="absolute top-1/2 left-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2 rounded-2xl border border-primary/25 bg-white px-4 py-3 shadow-2xl shadow-primary/20">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary ring-4 ring-primary/10">
          <img src={heroEmblemWhite} alt="Hero" className="h-8 w-8" />
        </div>
        <span
          className="block max-w-[160px] truncate text-sm font-semibold text-gray-900"
          title={orgLabel}>
          {orgLabel}
        </span>
      </div>
    </div>
  );
}

interface ConnectionLinesProps {
  hovered: RoleKey | null;
}

/**
 * Four-direction connection lines from the hub centre to each
 * cardinal. Split out of the parent so the active line for the
 * currently-hovered satellite can light up independently:
 *   - default: thin (2px), 50% opacity, dashed
 *   - active: thicker (3px), full opacity, solid, role-tinted
 *
 * Each line lives in the same SVG element so they share the same
 * coordinate space and stack identically; `pointer-events-none`
 * keeps them from intercepting hover events meant for the cards.
 */
function ConnectionLines({ hovered }: ConnectionLinesProps) {
  const lineFor = (role: RoleKey) => {
    const sat = SATELLITES.find(s => s.key === role);
    const isActive = hovered === role;
    return {
      className: `transition-all duration-200 ease-out ${
        isActive ? sat?.lineActiveClass ?? 'text-primary' : 'text-primary/40'
      }`,
      strokeWidth: isActive ? 3 : 2,
      strokeDasharray: isActive ? undefined : '4 6',
    };
  };

  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full" fill="none" aria-hidden>
      {/* hub → top (customers) */}
      <line
        x1="50%"
        y1="50%"
        x2="50%"
        y2="12%"
        stroke="currentColor"
        strokeLinecap="round"
        {...lineFor('customers')}
      />
      {/* hub → right (brokers) */}
      <line
        x1="50%"
        y1="50%"
        x2="88%"
        y2="50%"
        stroke="currentColor"
        strokeLinecap="round"
        {...lineFor('brokers')}
      />
      {/* hub → bottom (individuals) */}
      <line
        x1="50%"
        y1="50%"
        x2="50%"
        y2="88%"
        stroke="currentColor"
        strokeLinecap="round"
        {...lineFor('individuals')}
      />
      {/* hub → left (carriers) */}
      <line
        x1="50%"
        y1="50%"
        x2="12%"
        y2="50%"
        stroke="currentColor"
        strokeLinecap="round"
        {...lineFor('carriers')}
      />
    </svg>
  );
}

interface SatelliteNodeProps {
  satellite: RoleSatellite;
  onActivate: () => void;
  onDeactivate: () => void;
  title: string;
  microcopy: string;
}

/**
 * Interactive satellite card. Rendered as a real `<button>` purely
 * so the diagram is keyboard-focusable and hover/focus events can
 * drive the lifted `hovered` state in `NetworkDiagram` — the side
 * effect of that state is a single, focused thing: the connection
 * line from this card to the hub lights up in the role's identity
 * colour. The card itself stays visually static. No lift, no
 * scale, no copy swap — the highlighted line is the only change,
 * which keeps attention on the network metaphor rather than the
 * UI chrome.
 */
function SatelliteNode({
  satellite,
  onActivate,
  onDeactivate,
  title,
  microcopy,
}: SatelliteNodeProps) {
  const { Icon, position, cardBg, chip, ringClass } = satellite;

  return (
    <button
      type="button"
      onPointerEnter={onActivate}
      onPointerLeave={onDeactivate}
      onFocus={onActivate}
      onBlur={onDeactivate}
      className={`absolute z-10 ${POSITION_CLASSES[position]} flex w-24 flex-col items-center gap-1.5 rounded-2xl ${cardBg} px-2.5 py-2.5 shadow-[0_8px_24px_-10px_rgba(15,23,42,0.18)] backdrop-blur-sm transition-transform duration-200 ease-out hover:scale-105 focus-visible:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${ringClass}`}>
      {/* Solid coloured chip on a tinted card — the chip is the
          visual focal point ("this role"), the card is just the
          stage. Chip + card kept small so they don't crowd the
          hub or hide the connecting cross lines. */}
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg shadow-md ${chip}`}>
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
      <div className="flex flex-col items-center gap-0.5 text-center">
        <span className="text-[11px] font-semibold leading-tight text-gray-900">{title}</span>
        <span className="text-[10px] leading-tight text-gray-600">{microcopy}</span>
      </div>
    </button>
  );
}
